import { FieldPath, UseFormGetValues } from 'react-hook-form';

import {
  Commodity,
  Dropoff,
  LoadStopsItem,
  LoadStopsItemTypes,
  NormalizedDropoff,
  NormalizedLoad,
  NormalizedPickup,
  Pickup,
} from 'types/Load';
import { Undef } from 'types/UtilityTypes';

// Empty shell for a multi-stop route row (MGB Route section).
export function createEmptyMGBStop(
  stopType: LoadStopsItemTypes.Pickup | LoadStopsItemTypes.Dropoff,
  stopNumber: number
): LoadStopsItem {
  return {
    loadID: 0,
    stopType,
    stopNumber,
    externalTMSID: '',
    address: {
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      timezone: '',
      country: 'USA',
      isVerified: false,
    },
    contact: '',
    phone: '',
    email: '',
    businessHours: '',
    refNumber: '',
    readyTime: '',
    readyEndTime: '',
    mustDeliver: '',
    apptRequired: false,
    apptType: '',
    apptStartTime: '',
    apptEndTime: '',
    actualStartTime: '',
    actualEndTime: '',
    expectedStartTime: '',
    apptNote: '',
    timezone: '',
    additionalReferences: [],
  };
}

/** Stop datetime fields store strings; pickup/consignee may hold Date or string. */
function dateLikeToFormString(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  return String(value);
}

export function isMGBPickupStopType(stopType: string | undefined): boolean {
  return (
    String(stopType ?? '')
      .trim()
      .toLowerCase() === LoadStopsItemTypes.Pickup
  );
}

export function isMGBDropStopType(stopType: string | undefined): boolean {
  const t = String(stopType ?? '')
    .trim()
    .toLowerCase();
  return (
    t === LoadStopsItemTypes.Dropoff ||
    t === LoadStopsItemTypes.Delivery ||
    t === 'drop' ||
    t === 'consignee'
  );
}

/** Build default 2-stop route from classic pickup / consignee fields. */
export function buildMGBStopsFromPickupConsignee(
  pickup: Undef<Partial<Pickup> | Partial<NormalizedPickup>>,
  consignee: Undef<Partial<Dropoff> | Partial<NormalizedDropoff>>
): LoadStopsItem[] {
  const pu = createEmptyMGBStop(LoadStopsItemTypes.Pickup, 0);
  if (pickup) {
    pu.address = {
      ...pu.address,
      name: pickup.name ?? '',
      addressLine1: pickup.addressLine1 ?? '',
      addressLine2: pickup.addressLine2 ?? '',
      city: pickup.city ?? '',
      state: pickup.state ?? '',
      zip: pickup.zipCode ?? '',
      country: pickup.country?.trim() || 'USA',
    };
    pu.contact = pickup.contact ?? '';
    pu.phone = pickup.phone ?? '';
    pu.email = pickup.email ?? '';
    pu.refNumber = pickup.refNumber ?? '';
    pu.apptNote = pickup.apptNote ?? '';
    pu.readyTime = dateLikeToFormString(pickup.readyTime);
    pu.readyEndTime = dateLikeToFormString(pickup.readyTimeEnd);
    pu.apptStartTime = dateLikeToFormString(pickup.apptStartTime);
    pu.apptEndTime = dateLikeToFormString(pickup.apptEndTime);
    pu.externalTMSID = pickup.externalTMSID ?? '';
  }

  const drop = createEmptyMGBStop(LoadStopsItemTypes.Dropoff, 1);
  if (consignee) {
    drop.address = {
      ...drop.address,
      name: consignee.name ?? '',
      addressLine1: consignee.addressLine1 ?? '',
      addressLine2: consignee.addressLine2 ?? '',
      city: consignee.city ?? '',
      state: consignee.state ?? '',
      zip: consignee.zipCode ?? '',
      country: consignee.country?.trim() || 'USA',
    };
    drop.contact = consignee.contact ?? '';
    drop.phone = consignee.phone ?? '';
    drop.email = consignee.email ?? '';
    drop.refNumber = consignee.refNumber ?? '';
    drop.apptNote = consignee.apptNote ?? '';
    drop.mustDeliver = dateLikeToFormString(consignee.mustDeliver);
    drop.readyEndTime = dateLikeToFormString(consignee.mustDeliverEnd);
    drop.apptStartTime = dateLikeToFormString(consignee.apptStartTime);
    drop.apptEndTime = dateLikeToFormString(consignee.apptEndTime);
    drop.externalTMSID = consignee.externalTMSID ?? '';
  }

  return [pu, drop];
}

/** True when a route row has address or planned/appointment times (not an empty shell). */
function mgbStopHasRouteContent(stop: LoadStopsItem | undefined): boolean {
  if (!stop) {
    return false;
  }
  const address = stop.address;
  return Boolean(
    address?.name?.trim() ||
    address?.addressLine1?.trim() ||
    address?.city?.trim() ||
    address?.zip?.trim() ||
    String(stop.readyTime ?? '').trim() ||
    String(stop.mustDeliver ?? '').trim() ||
    String(stop.apptStartTime ?? '').trim()
  );
}

/** Ensure form has an ordered stops array (hydrate load or create defaults). */
export function ensureMGBRouteStops(values: NormalizedLoad): LoadStopsItem[] {
  const existing = values.stops ?? [];
  // createInitLoad() seeds two empty stops. Keep a real 2-stop or multi-stop
  // route; otherwise copy pickup/consignee (AI suggestion, classic hydrate).
  const keepExisting =
    existing.length > 2 ||
    (existing.length >= 2 && existing.some(mgbStopHasRouteContent));
  if (keepExisting) {
    return existing.map((s, i) => ({
      ...s,
      stopNumber: i,
      address: {
        ...s.address,
        country: s.address?.country?.trim() || 'USA',
      },
    }));
  }
  return buildMGBStopsFromPickupConsignee(values.pickup, values.consignee);
}

export function firstMGBPickupStopIndex(stops: LoadStopsItem[]): number {
  const idx = stops.findIndex((s) => isMGBPickupStopType(s.stopType));
  return idx >= 0 ? idx : 0;
}

export function lastMGBDropStopIndex(stops: LoadStopsItem[]): number {
  for (let i = stops.length - 1; i >= 0; i--) {
    if (isMGBDropStopType(stops[i]?.stopType)) {
      return i;
    }
  }
  return Math.max(stops.length - 1, 0);
}

/** Parse a commodity stop index for UI binding (invalid → undefined, no fallback). */
export function mgbCommodityStopFieldIndex(
  raw: unknown,
  stopsLength: number
): number | undefined {
  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0 || n >= stopsLength) {
    return undefined;
  }
  return Math.trunc(n);
}

/** Parse a stored commodity stop index without route-length fallback. */
function readMGBCommodityStopField(raw: unknown): number | undefined {
  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
}

/**
 * Read pick/drop from nested field paths (source of truth for selects).
 * useFieldArray parent-array snapshots can lag behind nested setValue updates.
 */
export function hydrateMGBCommodityStopFieldsFromForm(
  getValues: UseFormGetValues<NormalizedLoad>,
  commodities: Commodity[] | undefined
): Commodity[] {
  return (commodities ?? []).map((c, index) => {
    const pickupPath =
      `commodities.${index}.pickupStopNumber` as FieldPath<NormalizedLoad>;
    const dropPath =
      `commodities.${index}.dropStopNumber` as FieldPath<NormalizedLoad>;
    const pathPickup = readMGBCommodityStopField(getValues(pickupPath));
    const pathDrop = readMGBCommodityStopField(getValues(dropPath));
    const rowPickup = readMGBCommodityStopField(c.pickupStopNumber);
    const rowDrop = readMGBCommodityStopField(c.dropStopNumber);

    return {
      ...c,
      pickupStopNumber: pathPickup ?? rowPickup ?? c.pickupStopNumber,
      dropStopNumber: pathDrop ?? rowDrop ?? c.dropStopNumber,
    };
  });
}

/** Parse a commodity stop index; null/empty/invalid → fallback (never 0 from null). */
export function parseMGBStopIndex(
  raw: unknown,
  stopsLength: number,
  fallback: number
): number {
  if (raw === null || raw === undefined || raw === '') {
    return fallback;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0 || n >= stopsLength) {
    return fallback;
  }
  return Math.trunc(n);
}

function countMGBStopsByKind(stops: LoadStopsItem[]): {
  pickups: number;
  drops: number;
} {
  let pickups = 0;
  let drops = 0;
  for (const stop of stops) {
    if (isMGBPickupStopType(stop?.stopType)) {
      pickups += 1;
    } else if (isMGBDropStopType(stop?.stopType)) {
      drops += 1;
    }
  }
  return { pickups, drops };
}

function hasValidMGBRouteTopology(stops: LoadStopsItem[]): boolean {
  if (stops.length === 0) {
    return false;
  }
  return (
    isMGBPickupStopType(stops[0]?.stopType) &&
    isMGBDropStopType(stops[stops.length - 1]?.stopType)
  );
}

/** After move, first stop must be pickup and last must be drop. */
export function canMoveMGBStop(
  stops: LoadStopsItem[],
  from: number,
  to: number
): boolean {
  if (to < 0 || to >= stops.length || from === to) {
    return false;
  }
  const next = [...stops];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return hasValidMGBRouteTopology(next);
}

export function isOnlyPickupStopAt(
  stops: LoadStopsItem[],
  index: number
): boolean {
  const stop = stops[index];
  if (!isMGBPickupStopType(stop?.stopType)) {
    return false;
  }
  return countMGBStopsByKind(stops).pickups <= 1;
}

export function isOnlyDropStopAt(
  stops: LoadStopsItem[],
  index: number
): boolean {
  const stop = stops[index];
  if (!isMGBDropStopType(stop?.stopType)) {
    return false;
  }
  return countMGBStopsByKind(stops).drops <= 1;
}

/**
 * Remove is allowed when at least one pickup and one drop remain, and the
 * resulting route still starts with a pickup and ends with a drop.
 */
export function canRemoveMGBStop(
  stops: LoadStopsItem[],
  index: number
): boolean {
  if (index < 0 || index >= stops.length) {
    return false;
  }
  if (isOnlyPickupStopAt(stops, index) || isOnlyDropStopAt(stops, index)) {
    return false;
  }
  const next = [...stops];
  next.splice(index, 1);
  return hasValidMGBRouteTopology(next);
}

/**
 * Ensure commodity pickup/drop indices reference valid stop types and pickup
 * comes before drop in route order (required for interleaved multi-stop routes).
 */
export function ensureValidMGBCommodityLegIndices(
  remaining: LoadStopsItem[],
  pickup: number | null | undefined,
  drop: number | null | undefined
): { pickup: number; drop: number } {
  const defaultPickup = firstMGBPickupStopIndex(remaining);
  const defaultDrop = lastMGBDropStopIndex(remaining);
  const len = remaining.length;

  let p = parseMGBStopIndex(pickup, len, defaultPickup);
  let d = parseMGBStopIndex(drop, len, defaultDrop);

  if (!isMGBPickupStopType(remaining[p]?.stopType)) {
    p = defaultPickup;
  }
  if (!isMGBDropStopType(remaining[d]?.stopType)) {
    d = defaultDrop;
  }

  if (p < d) {
    return { pickup: p, drop: d };
  }

  for (let i = remaining.length - 1; i > p; i--) {
    if (isMGBDropStopType(remaining[i]?.stopType)) {
      return { pickup: p, drop: i };
    }
  }

  let prevPickup = -1;
  for (let i = 0; i < d; i++) {
    if (isMGBPickupStopType(remaining[i]?.stopType)) {
      prevPickup = i;
    }
  }
  if (prevPickup >= 0) {
    return { pickup: prevPickup, drop: d };
  }

  return { pickup: defaultPickup, drop: defaultDrop };
}

/**
 * After removing a route stop, remap commodity pick/drop indices onto the
 * remaining route. References to the deleted stop are re-resolved so pickup
 * always precedes drop (interleaved routes cannot use blind first/last fallbacks).
 */
export function remappedMGBCommodityStopIndicesAfterRemove(
  commodities: Undef<Commodity[]>,
  stops: LoadStopsItem[],
  removedIndex: number
): Commodity[] {
  const remaining = stops.filter((_, i) => i !== removedIndex);

  const mapIndex = (
    n: number | null | undefined
  ): number | null | undefined => {
    if (n == null || !Number.isFinite(n)) {
      return n;
    }
    if (n === removedIndex) {
      return undefined;
    }
    if (n > removedIndex) {
      return n - 1;
    }
    return n;
  };

  return (commodities ?? []).map((c) => {
    if (!c) {
      return c;
    }
    const leg = ensureValidMGBCommodityLegIndices(
      remaining,
      mapIndex(c.pickupStopNumber),
      mapIndex(c.dropStopNumber)
    );
    return {
      ...c,
      pickupStopNumber: leg.pickup,
      dropStopNumber: leg.drop,
    };
  });
}

/**
 * 1-based ordinal among stops of the same kind (pickup vs drop), matching
 * Route section titles ("Pickup 1", "Drop 1", "Drop 2") — not route index.
 */
export function mgbStopTypeOrdinal(
  stops: LoadStopsItem[],
  index: number
): number {
  const target = stops[index];
  if (!target) {
    return index + 1;
  }
  const targetIsPickup = isMGBPickupStopType(target.stopType);
  let ordinal = 0;
  for (let i = 0; i <= index; i++) {
    const isPickup = isMGBPickupStopType(stops[i]?.stopType);
    if (targetIsPickup ? isPickup : !isPickup) {
      ordinal += 1;
    }
  }
  return Math.max(ordinal, 1);
}

/** Expand the stop collapsible matching the first `stops.N` validation path. */
export function mgbActiveStopTabFromErrors(
  errors: ReadonlyArray<{ path: string }>
): string | undefined {
  for (const { path } of errors) {
    const match = /^stops\.(\d+)/.exec(String(path));
    if (match) {
      return `stop-${match[1]}`;
    }
  }
  return undefined;
}

/** UI label consistent across Route headers and commodity pick/drop selects. */
export function mgbStopLabel(
  stops: LoadStopsItem[],
  index: number,
  options?: { includeName?: boolean }
): string {
  const stop = stops[index];
  if (!stop) {
    return `Stop ${index + 1}`;
  }
  const typeLabel = isMGBPickupStopType(stop.stopType) ? 'Pickup' : 'Drop';
  const ordinal = mgbStopTypeOrdinal(stops, index);
  const base = `${typeLabel} ${ordinal}`;
  if (options?.includeName === false) {
    return base;
  }
  const name = stop.address?.name?.trim();
  return name ? `${base}: ${name}` : base;
}

/**
 * Mirror first pickup / last drop into classic pickup + consignee fields so
 * existing validation, Flash miles, and 2-stop consumers keep working.
 */
export function syncPickupConsigneeFromMGBStops(
  values: Pick<NormalizedLoad, 'stops' | 'pickup' | 'consignee'>
): {
  pickup: NormalizedPickup;
  consignee: NormalizedDropoff;
  moreThanTwoStops: boolean;
} {
  const stops = values.stops ?? [];
  if (stops.length < 2) {
    return {
      pickup: values.pickup,
      consignee: values.consignee,
      moreThanTwoStops: false,
    };
  }

  const pIdx = firstMGBPickupStopIndex(stops);
  const dIdx = lastMGBDropStopIndex(stops);
  const pu = stops[pIdx];
  const drop = stops[dIdx];

  return {
    moreThanTwoStops: stops.length > 2,
    pickup: {
      ...values.pickup,
      externalTMSID: pu.externalTMSID ?? '',
      name: pu.address?.name ?? '',
      addressLine1: pu.address?.addressLine1 ?? '',
      addressLine2: pu.address?.addressLine2 ?? '',
      city: pu.address?.city ?? '',
      state: pu.address?.state ?? '',
      zipCode: pu.address?.zip ?? '',
      country: pu.address?.country ?? 'USA',
      contact: pu.contact ?? '',
      phone: pu.phone ?? '',
      email: pu.email ?? '',
      refNumber: pu.refNumber ?? '',
      apptNote: pu.apptNote ?? '',
      readyTime: pu.readyTime as never,
      readyTimeEnd: pu.readyEndTime as never,
      apptStartTime: pu.apptStartTime as never,
      apptEndTime: pu.apptEndTime as never,
    },
    consignee: {
      ...values.consignee,
      externalTMSID: drop.externalTMSID ?? '',
      name: drop.address?.name ?? '',
      addressLine1: drop.address?.addressLine1 ?? '',
      addressLine2: drop.address?.addressLine2 ?? '',
      city: drop.address?.city ?? '',
      state: drop.address?.state ?? '',
      zipCode: drop.address?.zip ?? '',
      country: drop.address?.country ?? 'USA',
      contact: drop.contact ?? '',
      phone: drop.phone ?? '',
      email: drop.email ?? '',
      refNumber: drop.refNumber ?? '',
      apptNote: drop.apptNote ?? '',
      mustDeliver: drop.mustDeliver as never,
      mustDeliverEnd: drop.readyEndTime as never,
      apptStartTime: drop.apptStartTime as never,
      apptEndTime: drop.apptEndTime as never,
    },
  };
}
